import React, { useState, useEffect } from 'react'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import DownloadIcon from '../../assets/icons/DownloadIcon'
import { GLOBAL_CONSTANTS } from '../../constants/globalConstants.js'
import { columns } from '../../constants/ssr.js'
import { CommonTable, CommonModal } from '../common'
import SSRForm from './SSRForm'
import NoClientfound from '../../assets/icons/NoClientfound'
import { z } from 'zod'
import {
  saveAndUpdatePremiumFeature,
  getPremiumFeatureData,
  downloadPremiumFeature,
} from '../../api/premiumFeature.js'
import { useSnackbar } from '../../contexts/SnackbarProvider'
import { saveBlobAsFile } from '../../api/apiUtils.js'
import { sanitizeForLog } from '../../constants/index'
import { useDispatch } from 'react-redux'
import { setSelfServiceEnabled } from '../../store/slice/selfServiceSlice'

// Backend feature name for SSR (used in the API request URL/payload).
const SSR_FEATURE = 'SSR'

const ssrSchema = z.object({
  clientName: z.string().nonempty('Client Name is required'),
  enableSSR: z.boolean({
    required_error: 'Enable SSR is required',
    invalid_type_error: 'Enable SSR must be a boolean',
  }),
})

const SSR = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const [formData, setFormData] = useState({
    clientName: '',
    enableSSR: false,
  })
  const [formErrors, setFormErrors] = useState({})
  const [formLoading, setFormLoading] = useState(false)
  const [ssrData, setSsrData] = useState([])
  const [loading, setLoading] = useState(false)
  const [startDownloading, setStartDownloading] = useState(false)
  const { showSnackbar } = useSnackbar()
  const dispatch = useDispatch()

  const fetchTableData = async () => {
    setLoading(true)
    try {
      const res = await getPremiumFeatureData(SSR_FEATURE)
      setSsrData(res.data)
      setLoading(false)
    } catch (error) {
      console.error('API Error:', sanitizeForLog(error?.message || 'Unknown error'))
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTableData()
  }, [])

  const handleEdit = data => {
    setFormData({
      ...formData,
      uuid: data.uuid,
      clientName: data.clientName,
      clientUuid: data.clientUuid,
      enableSSR: data.isEnabled,
    })
    setModalOpen(true)
  }

  const handleAdd = () => {
    setFormData({
      clientName: '',
      enableSSR: ssrData?.length ? ssrData[0]?.isEnabled : false,
    })
    setModalOpen(true)
  }

  const handleFormSubmit = async e => {
    e.preventDefault()
    setFormLoading(true)

    try {
      // validate schema
      ssrSchema.parse(formData)
      setFormErrors({})

      const isEdit = Boolean(formData.uuid)

      const payload = {
        clientName: formData.clientName,
        clientUuid: formData.clientUuid,
        featureName: SSR_FEATURE,
        isEnabled: formData.enableSSR,
      }

      if (isEdit) {
        payload.uuid = formData.uuid
      }

      const res = await saveAndUpdatePremiumFeature(payload)

      if (res.success) {
        fetchTableData()
        dispatch(setSelfServiceEnabled(formData.enableSSR))

        showSnackbar(
          isEdit ? 'Updated successfully' : 'Added successfully',
          'success'
        )
      }

      // reset and close modal
      setTimeout(() => {
        setFormLoading(false)
        setModalOpen(false)
        setFormData({
          clientName: '',
          enableSSR: false,
        })
      }, 500)
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors = {}
        error.issues.forEach(err => {
          fieldErrors[err.path[0]] = err.message
        })
        setFormErrors(fieldErrors)
      } else {
        console.error('API Error:', sanitizeForLog(error?.message || 'Unknown error'))
        showSnackbar('Something went wrong', 'error')
      }

      setFormLoading(false)
    }
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setFormData({
      clientName: '',
      enableSSR: false,
    })
    setFormErrors({})
  }

  const handleNewUserInputChange = (field, value) => {
    setFormData(prev => {
      const updatedForm = { ...prev, [field]: value }

      try {
        ssrSchema.pick({ [field]: true }).parse({ [field]: value })

        setFormErrors(prevErrors => {
          const { [field]: removed, ...rest } = prevErrors
          return rest
        })
      } catch (error) {
        if (error instanceof z.ZodError) {
          setFormErrors(prevErrors => ({
            ...prevErrors,
            [field]: error.errors[0].message,
          }))
        }
      }

      return updatedForm
    })
  }

  const handleDownload = async () => {
    if (ssrData.length === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }

    setStartDownloading(true)

    try {
      const result = await downloadPremiumFeature(SSR_FEATURE)

      if (result?.success && result?.data) {
        saveBlobAsFile(result.data, 'ssr.xlsx')
        showSnackbar('Download successful', 'success')
      } else {
        showSnackbar(result?.error || 'Download failed', 'error')
      }
    } catch (error) {
      console.error('Download API Error:', sanitizeForLog(error?.message || 'Unknown error'))
      showSnackbar('Something went wrong', 'error')
    }

    setStartDownloading(false)
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '32px',
          py: '12px',
        }}
      >
        {/* Left side - Title */}
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            color: 'var(--forbrand-secondary)',
            fontWeight: 500,
          }}
        >
          SSR
        </Typography>

        {/* Right side - Action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_EXCEL}>
            <span className="disabled:cursor-not-allowed">
              <IconButton
                onClick={handleDownload}
                disabled={loading || startDownloading}
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>

            <Button
              variant="contained"
              onClick={handleAdd}
              sx={{
                backgroundColor: 'var(--forbrand-bg-variant)',
                color: 'var(--forbrand-brand-varient)',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              Add
            </Button>
        
        </Box>
      </Box>

      {/* Table */}
      <Box>
        <CommonTable
          columns={columns}
          data={ssrData}
          loading={loading}
          onEdit={handleEdit}
          emptyMessage="No Client Added"
          emptyIcon={<NoClientfound />}
        />
      </Box>

      {/* modal */}
      <CommonModal
        open={modalOpen}
        onClose={handleModalClose}
        title={'SSR Configuration'}
        maxWidth="sm"
      >
        <SSRForm
          formData={formData}
          formErrors={formErrors}
          formLoading={formLoading}
          setFormData={setFormData}
          isEdit={false}
          setFormErrors={setFormErrors}
          handleFormSubmit={handleFormSubmit}
          onInputChange={handleNewUserInputChange}
        />
      </CommonModal>
    </Box>
  )
}

export default SSR
